import { useMutation, useQuery } from "@tanstack/react-query";
import { useId, useRef, useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText } from "../../ui";
import { Dialog } from "../../ui/overlay";
import { useToast } from "../../ui/toast";
import { HELP } from "./keyboard";

/** A one-field dialog for "Add a tag" and "Move to folder", with suggestions. */
export function PromptDialog({
  title,
  label,
  submitLabel,
  suggestions,
  onSubmit,
  onClose,
}: {
  title: string;
  label: string;
  submitLabel: string;
  suggestions: readonly string[];
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (v === "" || v.length > 100) {
      setError("Enter a name of up to 100 characters.");
      return;
    }
    onSubmit(v);
    onClose();
  };
  return (
    <Dialog title={title} onClose={onClose} initialFocus={input}>
      <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
        <label htmlFor={id} className="text-sm font-medium text-text">
          {label}
        </label>
        <input
          id={id}
          ref={input}
          list={`${id}-list`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="min-h-9 rounded-md border border-border bg-surface px-3 text-sm text-text"
        />
        <datalist id={`${id}-list`}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        {error === null ? null : <ErrorText>{error}</ErrorText>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary">
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Shares a conversation into a workspace channel as an email chip (`chat.post` with a ref). */
export function ShareDialog({
  workspaceId,
  threadId,
  subject,
  onClose,
}: {
  workspaceId: string;
  threadId: string;
  subject: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const toast = useToast();
  const id = useId();
  const channels = useQuery(trpc.chat.channels.queryOptions({ workspaceId }));
  const post = useMutation(trpc.chat.post.mutationOptions());
  const [channelId, setChannelId] = useState("");
  const [note, setNote] = useState("");
  const list = channels.data === undefined ? [] : channels.data;
  const chosen = channelId === "" && list[0] !== undefined ? list[0].id : channelId;
  const label = subject === "" ? "(no subject)" : subject.slice(0, 200);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (chosen === "") return;
    post.mutate(
      { workspaceId, channelId: chosen, body: note.trim() === "" ? `Shared an email: ${label}` : note.trim(), refs: [{ kind: "email", id: threadId, label }] },
      {
        onSuccess: () => {
          const name = list.find((c) => c.id === chosen)?.name;
          toast({ message: name === undefined ? "Shared." : `Shared to #${name}.` });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog title="Share to chat" description="Members see the subject and can ask you about it; the email stays in your mailbox." onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {channels.isPending ? <p className="text-sm text-muted">Loading channels…</p> : null}
        {channels.isError ? <ErrorText>{errorMessage(channels.error)}</ErrorText> : null}
        {channels.isSuccess && list.length === 0 ? <p className="text-sm text-muted">This workspace has no channels yet.</p> : null}
        {list.length > 0 ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={`${id}-channel`} className="text-sm font-medium text-text">
              Channel
            </label>
            <select
              id={`${id}-channel`}
              value={chosen}
              onChange={(e) => setChannelId(e.target.value)}
              className="min-h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
            >
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.kind === "dm" ? c.name : `#${c.name}`}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-note`} className="text-sm font-medium text-text">
            Message (optional)
          </label>
          <textarea id={`${id}-note`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text" />
        </div>
        <p className="rounded-md bg-surface-2 px-2 py-1 text-xs text-muted">Email: {label}</p>
        {post.isError ? <ErrorText>{errorMessage(post.error)}</ErrorText> : null}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={chosen === "" || post.isPending}>
            Share
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <Dialog title="Keyboard shortcuts" onClose={onClose}>
      <table className="w-full text-left text-sm">
        <tbody>
          {HELP.map((h) => (
            <tr key={h.keys} className="border-t border-border">
              <th scope="row" className="py-1.5 pr-4 font-mono text-xs font-semibold text-text">
                {h.keys}
              </th>
              <td className="py-1.5 text-muted">{h.does}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Dialog>
  );
}
